'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';

const questions = [
  'How much should we raise?',
  'When should we raise?',
  'Which investors should we approach?',
  'How should we position the company?',
  'What makes our opportunity compelling?',
  'Which investors actually invest in our sector?',
  'Who is the right investor for our stage?',
  'How do we create momentum around the round?',
  'How should we manage investor conversations?',
  'What should we do when investors say "not yet"?',
];

export default function ChallengeSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 border-t border-primary/10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <p className="section-label scroll-fade mb-5">The Challenge</p>
          <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6">
            The right company can still struggle{' '}
            <span className="font-serif italic text-primary">to raise capital.</span>
          </h2>
          <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2">
            Fundraising is rarely just about having a good business. Founders often face questions
            such as:
          </p>
        </div>

        {/* Questions bento grid */}
        {/* BENTO AUDIT: 10 questions, 2 columns, 5 rows */}
        {/* Row 1: [col-1: Q1 cs-1] [col-2: Q2 cs-1] */}
        {/* Row 2: [col-1: Q3 cs-1] [col-2: Q4 cs-1] */}
        {/* Row 3: [col-1: Q5 cs-1] [col-2: Q6 cs-1] */}
        {/* Row 4: [col-1: Q7 cs-1] [col-2: Q8 cs-1] */}
        {/* Row 5: [col-1: Q9 cs-1] [col-2: Q10 cs-1] */}
        {/* Placed 10/10 ✓ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-16">
          {questions?.map((q, i) => (
            <div
              key={i}
              className={`bento-card rounded-2xl px-6 py-5 flex items-start gap-4 scroll-fade`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="text-primary font-serif italic text-sm mt-0.5 shrink-0">
                {String(i + 1)?.padStart(2, '0')}
              </span>
              <p className="text-sm text-foreground/80 font-light leading-relaxed">{q}</p>
            </div>
          ))}
        </div>

        {/* Resolution statement */}
        <div className="max-w-2xl border-l-2 border-primary/40 pl-8 scroll-fade">
          <p className="text-base sm:text-lg text-foreground font-light leading-relaxed">
            Aregn Capital helps founders answer these questions{' '}
            <span className="text-primary font-medium">before and during</span> the fundraising
            process.
          </p>
        </div>
      </div>
    </section>
  );
}
